import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Card,
    CardContent,
    IconButton,
    Chip,
    LinearProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Tabs,
    Tab,
    Autocomplete
} from '@mui/material';
import {
    AutoAwesome as AiIcon,
    Close as CloseIcon,
    Add as AddIcon,
    ExpandMore as ExpandIcon,
    CheckCircle as CheckIcon,
    Edit as EditIcon,
    TextFields as TextIcon,
    Link as LinkIcon,
    PictureAsPdf as PdfIcon,
    Article as LessonContentIcon
} from '@mui/icons-material';
import { useDataProvider } from 'react-admin';
import { devLog } from '../../utils/devLog';

interface Question {
    question?: string;
    options?: string[];
    correctAnswer?: number;
    explanation?: string;
    text?: string;
    sampleAnswer?: string;
    gradingCriteria?: string[];
    left?: string;
    right?: string;
    zone?: string;
    items?: string[];
}

interface QuizGeneratorProps {
    open: boolean;
    onClose: () => void;
    onQuestionsGenerated: (questions: Question[], questionType: string) => void;
    defaultQuestionType?: string; // Optional: if provided, locks the question type
    lessonLearningContent?: string; // Optional: HTML des Lerninhalt-Felds der Lektion (#310)
}

const countLabel = (questionType: string): string => {
    switch (questionType) {
        case 'fill_in_blank':
            return 'Anzahl Lücken';
        case 'matching':
            return 'Anzahl Paare';
        case 'drag_drop':
            return 'Anzahl Kategorien';
        default:
            return 'Anzahl Fragen';
    }
};

const stripHtml = (html: string): string => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

const QuizGenerator: React.FC<QuizGeneratorProps> = ({ open, onClose, onQuestionsGenerated, defaultQuestionType, lessonLearningContent }) => {
    const dataProvider = useDataProvider();

    const hasLessonContent = !!stripHtml(lessonLearningContent || '');

    // Input source: 'lesson', 'text', 'url', 'pdf'
    const [sourceType, setSourceType] = useState<'lesson' | 'text' | 'url' | 'pdf'>(hasLessonContent ? 'lesson' : 'text');
    const [content, setContent] = useState('');
    const [url, setUrl] = useState('');
    const [selectedPdf, setSelectedPdf] = useState<any>(null);
    const [pdfFiles, setPdfFiles] = useState<any[]>([]);
    const [loadingPdfs, setLoadingPdfs] = useState(false);

    const [questionCount, setQuestionCount] = useState(5);
    const [questionType, setQuestionType] = useState(defaultQuestionType || 'multiple_choice');
    const [difficulty, setDifficulty] = useState('medium');
    const [useSimpleLanguage, setUseSimpleLanguage] = useState(false);
    const [continuousText, setContinuousText] = useState(defaultQuestionType === 'fill_in_blank');
    const [generating, setGenerating] = useState(false);
    const [generatedQuestions, setGeneratedQuestions] = useState<Question[]>([]);
    const [error, setError] = useState('');

    // Load PDF files when dialog opens
    useEffect(() => {
        if (open && sourceType === 'pdf') {
            loadPdfFiles();
        }
    }, [open, sourceType]);

    // Update questionType and continuousText when defaultQuestionType changes
    useEffect(() => {
        if (defaultQuestionType) {
            setQuestionType(defaultQuestionType);
            if (defaultQuestionType === 'fill_in_blank') {
                setContinuousText(true);
            }
        }
    }, [defaultQuestionType]);

    const loadPdfFiles = async () => {
        setLoadingPdfs(true);
        try {
            const response = await dataProvider.getList('media_files', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'createdAt', order: 'DESC' },
                filter: {}
            });

            devLog('All media files:', response.data);

            // Filter only PDF files by checking mimeType or originalFilename
            const pdfs = response.data.filter((file: any) => {
                const isPdfByMime = file.mimeType?.toLowerCase() === 'application/pdf';
                const isPdfByFilename = file.originalFilename?.toLowerCase().endsWith('.pdf');
                return isPdfByMime || isPdfByFilename;
            });

            devLog('Filtered PDF files:', pdfs);
            setPdfFiles(pdfs);
        } catch (err) {
            console.error('Error loading PDF files:', err);
            setError('Fehler beim Laden der PDF-Dateien');
        } finally {
            setLoadingPdfs(false);
        }
    };

    const handleGenerate = async () => {
        // Validate input based on source type
        if (sourceType === 'lesson' && !hasLessonContent) {
            setError('Der Lerninhalt der Lektion ist leer.');
            return;
        }
        if (sourceType === 'text' && !content.trim()) {
            setError('Bitte geben Sie Text ein, aus dem Fragen generiert werden sollen.');
            return;
        }
        if (sourceType === 'url' && !url.trim()) {
            setError('Bitte geben Sie eine URL ein.');
            return;
        }
        if (sourceType === 'pdf' && !selectedPdf) {
            setError('Bitte wählen Sie ein PDF-Dokument aus.');
            return;
        }

        setGenerating(true);
        setError('');
        setGeneratedQuestions([]);

        try {
            // Use the same API URL as configured in dataProvider
            const apiUrl = `${process.env.REACT_APP_API_URL}/api`;

            const requestBody: any = {
                questionCount,
                questionType,
                difficulty,
                sourceType: sourceType === 'lesson' ? 'text' : sourceType,
                useSimpleLanguage,
                continuousText
            };

            // Add appropriate content based on source type
            if (sourceType === 'lesson') {
                requestBody.content = stripHtml(lessonLearningContent || '');
            } else if (sourceType === 'text') {
                requestBody.content = content;
            } else if (sourceType === 'url') {
                requestBody.url = url;
            } else if (sourceType === 'pdf') {
                requestBody.mediaFileId = selectedPdf.id;
            }

            const response = await fetch(`${apiUrl}/ai/generate-questions`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const body = await response.json().catch(() => null);
                setError(
                    body?.error
                        ? `Fehler beim Generieren der Fragen: ${body.error}`
                        : 'Fehler beim Generieren der Fragen. Bitte versuchen Sie es später erneut.'
                );
                return;
            }

            const data = await response.json();

            if (data.success && data.questions && data.questions.length > 0) {
                setGeneratedQuestions(data.questions);
            } else {
                setError('Keine Fragen konnten generiert werden. Bitte versuchen Sie es erneut.');
            }

        } catch (err) {
            console.error('Generation error:', err);
            setError('Fehler beim Generieren der Fragen. Bitte versuchen Sie es später erneut.');
        } finally {
            setGenerating(false);
        }
    };

    const handleUseQuestions = () => {
        onQuestionsGenerated(generatedQuestions, questionType);
        handleClose();
    };

    const handleClose = () => {
        setSourceType(hasLessonContent ? 'lesson' : 'text');
        setContent('');
        setUrl('');
        setSelectedPdf(null);
        setGeneratedQuestions([]);
        setError('');
        onClose();
    };

    const renderQuestionPreview = (question: Question, index: number) => {
        if (questionType === 'multiple_choice') {
            return (
                <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Chip label={`Frage ${index + 1}`} size="small" color="primary" />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                {question.question}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {question.options?.map((option, optIndex) => (
                                <Box
                                    key={optIndex}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        p: 1,
                                        borderRadius: 1,
                                        bgcolor: question.correctAnswer === optIndex ? 'success.light' : 'background.default'
                                    }}
                                >
                                    {question.correctAnswer === optIndex && <CheckIcon color="success" fontSize="small" />}
                                    <Typography variant="body2">
                                        {String.fromCharCode(65 + optIndex)}) {option}
                                    </Typography>
                                </Box>
                            ))}
                            {question.explanation && (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    <Typography variant="caption">
                                        <strong>Erklärung:</strong> {question.explanation}
                                    </Typography>
                                </Alert>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            );
        } else if (questionType === 'fill_in_blank') {
            return (
                <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip label={`Lückentext ${index + 1}`} size="small" color="primary" />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace' }}>
                            {question.text}
                        </Typography>
                        {question.explanation && (
                            <Alert severity="info">
                                <Typography variant="caption">
                                    <strong>Erklärung:</strong> {question.explanation}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            );
        } else if (questionType === 'matching') {
            return (
                <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip label={`Paar ${index + 1}`} size="small" color="primary" />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="body2" sx={{ flex: 1 }}>{question.left}</Typography>
                            <Typography variant="body2" color="text.secondary">→</Typography>
                            <Typography variant="body2" sx={{ flex: 1 }}>{question.right}</Typography>
                        </Box>
                        {question.explanation && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Erklärung:</strong> {question.explanation}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            );
        } else if (questionType === 'drag_drop') {
            return (
                <Card key={index} sx={{ mb: 2 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Chip label={`Kategorie ${index + 1}`} size="small" color="primary" />
                            <Typography variant="body2">{question.zone}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {question.items?.map((item, itemIndex) => (
                                <Chip key={itemIndex} label={item} size="small" variant="outlined" />
                            ))}
                        </Box>
                        {question.explanation && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    <strong>Erklärung:</strong> {question.explanation}
                                </Typography>
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            );
        } else {
            // free_text
            return (
                <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                            <Chip label={`Frage ${index + 1}`} size="small" color="primary" />
                            <Typography variant="body2" sx={{ flex: 1 }}>
                                {question.question}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {question.sampleAnswer && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                        Musterantwort:
                                    </Typography>
                                    <Typography variant="body2">
                                        {question.sampleAnswer}
                                    </Typography>
                                </Box>
                            )}
                            {question.gradingCriteria && question.gradingCriteria.length > 0 && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary" gutterBottom>
                                        Bewertungskriterien:
                                    </Typography>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {question.gradingCriteria.map((criterion, i) => (
                                            <li key={i}>
                                                <Typography variant="body2">{criterion}</Typography>
                                            </li>
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </AccordionDetails>
                </Accordion>
            );
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AiIcon color="primary" />
                        <Typography variant="h6">KI Quiz-Generator</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Alert severity="info" sx={{ mb: 3 }}>
                    Geben Sie Lernmaterial ein und lassen Sie die KI automatisch Fragen daraus generieren.
                    Die generierten Fragen können Sie direkt in Ihre Lektion übernehmen.
                </Alert>

                {defaultQuestionType && (
                    <Alert severity="success" sx={{ mb: 3 }}>
                        Fragetyp automatisch auf <strong>
                            {defaultQuestionType === 'multiple_choice' && 'Multiple Choice'}
                            {defaultQuestionType === 'fill_in_blank' && 'Lückentext'}
                            {defaultQuestionType === 'free_text' && 'Freitext'}
                            {defaultQuestionType === 'matching' && 'Zuordnung'}
                            {defaultQuestionType === 'drag_drop' && 'Drag & Drop'}
                        </strong> gesetzt (passend zur aktuellen Lektion).
                    </Alert>
                )}

                {!generatedQuestions.length ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Source Type Selection */}
                        <Tabs value={sourceType} onChange={(e, v) => setSourceType(v)} centered>
                            {hasLessonContent && (
                                <Tab icon={<LessonContentIcon />} label="Lerninhalt der Lektion" value="lesson" />
                            )}
                            <Tab icon={<TextIcon />} label="Text eingeben" value="text" />
                            <Tab icon={<LinkIcon />} label="URL verwenden" value="url" />
                            <Tab icon={<PdfIcon />} label="PDF aus Mediathek" value="pdf" />
                        </Tabs>

                        {/* Lesson Learning Content (read-only preview) */}
                        {sourceType === 'lesson' && (
                            <TextField
                                label="Lerninhalt der Lektion"
                                multiline
                                rows={8}
                                fullWidth
                                value={stripHtml(lessonLearningContent || '')}
                                slotProps={{ input: { readOnly: true } }}
                                helperText="Die Fragen werden aus dem Lerninhalt-Feld dieser Lektion generiert. Zum Ändern das Feld im Editor bearbeiten."
                            />
                        )}

                        {/* Text Input */}
                        {sourceType === 'text' && (
                            <TextField
                                label="Lerninhalt"
                                multiline
                                rows={8}
                                fullWidth
                                required
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="Fügen Sie hier den Text ein, aus dem Fragen generiert werden sollen..."
                                helperText="Je mehr Details und Struktur der Inhalt hat, desto besser werden die generierten Fragen."
                            />
                        )}

                        {/* URL Input */}
                        {sourceType === 'url' && (
                            <Box>
                                <TextField
                                    label="URL"
                                    fullWidth
                                    required
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://de.wikipedia.org/wiki/..."
                                    helperText="Geben Sie die URL eines Artikels, Blogbeitrags oder einer Webseite ein"
                                    type="url"
                                />
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Die KI wird den Inhalt der Webseite abrufen und daraus Fragen generieren.
                                    Dies funktioniert am besten mit textbasierten Seiten wie Wikipedia, Blogs oder Dokumentationen.
                                </Alert>
                            </Box>
                        )}

                        {/* PDF Selection */}
                        {sourceType === 'pdf' && (
                            <Box>
                                {loadingPdfs ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                        <LinearProgress sx={{ width: '100%' }} />
                                    </Box>
                                ) : (
                                    <>
                                        <Autocomplete
                                            options={pdfFiles}
                                            getOptionLabel={(option) => option.originalFilename || 'Unbenannt'}
                                            value={selectedPdf}
                                            onChange={(e, newValue) => setSelectedPdf(newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="PDF-Dokument auswählen"
                                                    required
                                                    helperText={`${pdfFiles.length} PDF-Dokument(e) verfügbar`}
                                                />
                                            )}
                                            renderOption={(props, option) => (
                                                <Box component="li" {...props}>
                                                    <PdfIcon sx={{ mr: 2, color: 'error.main' }} />
                                                    <Box>
                                                        <Typography variant="body2">{option.originalFilename}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {new Date(option.createdAt).toLocaleDateString('de-DE')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            )}
                                            fullWidth
                                        />
                                        {pdfFiles.length === 0 && (
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                Keine PDF-Dokumente in der Mediathek gefunden. Bitte laden Sie zuerst ein PDF-Dokument hoch.
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}

                        <Box sx={{ display: 'grid', gridTemplateColumns: defaultQuestionType ? '1fr' : '1fr 1fr', gap: 2 }}>
                            {!defaultQuestionType && (
                                <FormControl fullWidth>
                                    <InputLabel>Fragetyp</InputLabel>
                                    <Select
                                        value={questionType}
                                        label="Fragetyp"
                                        onChange={(e) => setQuestionType(e.target.value)}
                                    >
                                        <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                                        <MenuItem value="fill_in_blank">Lückentext</MenuItem>
                                        <MenuItem value="free_text">Freitext</MenuItem>
                                        <MenuItem value="matching">Zuordnung</MenuItem>
                                        <MenuItem value="drag_drop">Drag &amp; Drop</MenuItem>
                                    </Select>
                                </FormControl>
                            )}

                            <TextField
                                label={countLabel(questionType)}
                                type="number"
                                fullWidth
                                value={questionCount}
                                onChange={(e) => setQuestionCount(parseInt(e.target.value) || 5)}
                                inputProps={{ min: 1, max: 20 }}
                            />
                        </Box>

                        <FormControl fullWidth>
                            <InputLabel>Schwierigkeitsgrad</InputLabel>
                            <Select
                                value={difficulty}
                                label="Schwierigkeitsgrad"
                                onChange={(e) => setDifficulty(e.target.value)}
                            >
                                <MenuItem value="easy">Einfach</MenuItem>
                                <MenuItem value="medium">Mittel</MenuItem>
                                <MenuItem value="hard">Schwer</MenuItem>
                            </Select>
                        </FormControl>


                        <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <input
                                    type="checkbox"
                                    id="simple-language"
                                    checked={useSimpleLanguage}
                                    onChange={(e) => setUseSimpleLanguage(e.target.checked)}
                                    style={{ cursor: 'pointer' }}
                                />
                                <label htmlFor="simple-language" style={{ cursor: 'pointer' }}>
                                    <Typography variant="body2">
                                        <strong>Einfache Sprache verwenden</strong> (nach ISO 24495-1)
                                    </Typography>
                                </label>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 3, display: 'block', mt: 0.5 }}>
                                Die KI verwendet kurze Sätze, einfache Wörter und eine klare Struktur für bessere Verständlichkeit.
                            </Typography>
                        </Box>

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Alert severity="success" sx={{ mb: 3 }}>
                            {questionType === 'fill_in_blank'
                                ? 'Lückentext erfolgreich generiert!'
                                : `${generatedQuestions.length} Frage(n) erfolgreich generiert!`}
                        </Alert>

                        <Typography variant="h6" gutterBottom>
                            Vorschau der generierten Fragen:
                        </Typography>

                        <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                            {generatedQuestions.map((question, index) =>
                                renderQuestionPreview(question, index)
                            )}
                        </Box>
                    </Box>
                )}

                {generating && (
                    <Box sx={{ mt: 3 }}>
                        <LinearProgress />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                            Generiere Fragen mit KI... Dies kann einige Sekunden dauern.
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {!generatedQuestions.length ? (
                    <>
                        <Button onClick={handleClose}>Abbrechen</Button>
                        <Button
                            variant="contained"
                            startIcon={<AiIcon />}
                            onClick={handleGenerate}
                            disabled={
                                generating ||
                                (sourceType === 'text' && !content.trim()) ||
                                (sourceType === 'url' && !url.trim()) ||
                                (sourceType === 'pdf' && !selectedPdf)
                            }
                        >
                            {generating ? 'Generiere...' : questionType === 'fill_in_blank' ? 'Lückentext generieren' : 'Fragen generieren'}
                        </Button>
                    </>
                ) : (
                    <>
                        <Button onClick={() => setGeneratedQuestions([])}>
                            Neu generieren
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleUseQuestions}
                        >
                            Fragen übernehmen ({generatedQuestions.length})
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default QuizGenerator;
